import type {CSSProperties} from 'react';
import {space} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeWonMomentLook = 'won-moment-save' | 'won-moment-keep';

const WON_MOMENT_LOOKS = new Set<string>(['won-moment-save', 'won-moment-keep']);
export const isWedgeWonMomentLook = (look: string | undefined): look is WedgeWonMomentLook => look !== undefined && WON_MOMENT_LOOKS.has(look);

/* Released won moment card (the close celebration over the fixed scrim): two full-width blocks stacked in the card. Save / share the win is a 50px
   tall, 14px radius flex row with an 8px gap, the accent fill, the on-accent ink and 15px bold type. Held with `disabled` (the card is rendering
   the image) it dims to 70% and takes the default cursor. Keep going has no fill and no border, 14px by 14px padding, the secondary label ink,
   14px medium type and 4px above it. Both read the exact legacy roles and keep the declaration order of the source, so the shipped ink rules keep
   matching the serialized text. The on-accent ink is the bare variable: the Wedge scope always defines it, so the source fallback is never read.
   Save draws no transparent target and no anchor (wedgeSettingsDrawsHit, and a `content: none` rule naming the look): it is already 50px tall and
   full width, and a browser check with the anchor on, with the confetti live, drew 881 to 893 different pixels inside its box and added 425 to 439
   px of tap area (the rounded corners and a row outside the box). Keep going keeps the default target and its anchor: it is taller than 44px, so
   its region sits inside the box, and the 4px between the blocks stays clear. */
const SAVE_HEIGHT = 50, SAVE_RADIUS = 14, SAVE_GAP = 8, BUSY_OPACITY = 0.7;

export function wedgeWonMomentStyle(look: WedgeWonMomentLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  switch (look) {
    case 'won-moment-save': return {
      width: '100%', height: SAVE_HEIGHT, borderRadius: SAVE_RADIUS,
      background: r('accent'), color: r('onAccent'), border: 0, cursor: disabled ? 'default' : 'pointer',
      fontSize: 15, fontWeight: 700, fontFamily: r('font'),
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: SAVE_GAP,
      opacity: disabled ? BUSY_OPACITY : 1,
    };
    case 'won-moment-keep': return {
      width: '100%', background: 'none', border: 0,
      color: r('labelSec'), fontSize: 14, fontWeight: 500,
      padding: 14, marginTop: space[1], cursor: 'pointer', fontFamily: r('font'),
    };
  }
}
